import debounce from 'lodash.debounce';
import React from 'react';
import PropTypes from 'prop-types';
import { AiOutlineLeft, AiOutlineRight } from 'react-icons/ai'
import styles from '../styles/horizontalSlider.module.css'

const { node, func } = PropTypes;

const arrowWidth = 20;
const minOffset = 120;

class HorizontalSlider extends React.Component {
  static displayName = 'HorizontalSlider';

  static propTypes = {
    children: node.isRequired,
    renderLeftArrow: func,
    renderRightArrow: func,
  };

  static defaultProps = {
    renderLeftArrow: (disabled) => <AiOutlineLeft color={disabled? '#dedede' : '#948c8c'} size='1em'/>,
    renderRightArrow: (disabled) => <AiOutlineRight color={disabled? '#dedede' : '#948c8c'} size='1em'/>,
  };

  constructor(props) {
    super(props);

    this.setSliderRef = element => {
      this.slider = element;
    };
    this.setSliderContentRef = element => {
      this.sliderContent = element;
    };

    this.state = {
      marginLeft: 0,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize());
    this.resetMargin();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize());
  }

  resetMargin = () => {
    if (this.slider && this.sliderContent) {
      this.setState({ marginLeft: 0 });
    }
  }

  //   slider width
  //        |  content width
  //        v        |
  // ~~~~~~~~~~~~~~  v
  // ~~~~~~~~~~~~~~~~~~~~
  // ______________
  // |             |----|
  // |             |

  // if we are currently paginated to the right less than / equal the width of
  // the slider container, then we can paginate back left all the way;
  // otherwise there is not enough space, so we only shift left the amount of
  // the width of the slider container

  handleLeftClicked = () => {
    const currentMarginLeft = this.state.marginLeft;
    const sliderWidth = this.slider.offsetWidth;
    let marginLeft;

    marginLeft = Math.max(currentMarginLeft - Math.min(sliderWidth, minOffset), 0) ;

    this.setState({ marginLeft });
  }

  // if the amount of width left is less than / equal the width of the slider
  // container, we can shift left enough to show the entire remaining width;
  // otherwise we shift the length of the slider container; note the extra
  // 20px to account for margins for the arrows

  handleRightClicked = () => {
    const currentMarginLeft = this.state.marginLeft;
    const sliderWidth = this.slider.offsetWidth;
    const contentWidth = this.sliderContent.offsetWidth;
    const remainingWidth = contentWidth - (sliderWidth - arrowWidth) - currentMarginLeft;
    let marginLeft;

    if (remainingWidth > 0) {
      if (remainingWidth <= sliderWidth) {
        marginLeft = currentMarginLeft + Math.min(remainingWidth, minOffset);
      } else {
        marginLeft = currentMarginLeft + Math.min(sliderWidth, minOffset);
      }
    } else {
      marginLeft = currentMarginLeft;
    }

    this.setState({ marginLeft });
  }

  handleResize = () => {
    this.updateFn = this.updateFn || debounce(() => { this.resetMargin(); }, 200);
    return this.updateFn;
  }

  // Show left arrow if we are tabbed over at all (margin left not 0)

  renderLeftArrow = () => {
    const disabled = !(this.state.marginLeft !== 0)
    return (
        <span className={[styles.caret, styles.caretLeft].join(' ')} onClick={this.handleLeftClicked}>
            {this.props.renderLeftArrow(disabled)}
        </span>
    );
  }


  // When the 'content' of a slider exceeds the width of the slider itself
  // the right arrow should show; remaining width is the current width of
  // the content minus the width of the slider container

  renderRightArrow = () => {
    const currentMarginLeft = this.state.marginLeft;
    const sliderWidth = this.slider ? this.slider.offsetWidth : 0;
    const contentWidth = this.sliderContent ? this.sliderContent.offsetWidth : 0;
    const remainingWidth = contentWidth - (sliderWidth - arrowWidth) - currentMarginLeft;
    const disabled = !(remainingWidth > 0)
    return (
        <span className={[styles.caret, styles.caretRight].join(' ')} onClick={this.handleRightClicked}>
            {this.props.renderRightArrow(disabled)}
        </span>
    );
  }

  render = () => {
    return (
      <div className={styles.componentSlider} ref={this.setSliderRef}>
        {this.renderLeftArrow()}
        <div className={styles.sliderContainer}>
          <div
            className={styles.sliderContent}
            ref={this.setSliderContentRef}
            style={{ marginLeft: `-${this.state.marginLeft}px` }}
          >
            {this.props.children}
          </div>
        </div>
        {this.renderRightArrow()}
      </div>
    );
  }
}

export default HorizontalSlider;